import React, { useState, useEffect } from "react";
import Grid from "@material-ui/core/Grid";
import List from "@material-ui/core/List";
import { DragDropContext } from "react-beautiful-dnd";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import AddIcon from "@material-ui/icons/Add";
import style from "../../assets/jss/disal-main/Layouts/Route";
import Checkbox from "@material-ui/core/Checkbox";
import Column from "./Column";
import { useRoutes } from "../../Context/RoutesContext";

const useStyles = makeStyles(style);

const DAYS_OF_WEEK = [
  { dayId: 1, dayName: "LUNES", short: "Lu" },
  { dayId: 2, dayName: "MARTES", short: "Ma" },
  { dayId: 3, dayName: "MIÉRCOLES", short: "Mi" },
  { dayId: 4, dayName: "JUEVES", short: "Ju" },
  { dayId: 5, dayName: "VIERNES", short: "Vi" },
  { dayId: 6, dayName: "SÁBADO", short: "Sa" },
  { dayId: 7, dayName: "DOMINGO", short: "Do" },
];

const FormRoutes = () => {
  const classes = useStyles();
  const {
    selected,
    setSelected,
    vehicles,
    points,
    doSave,
  } = useRoutes();

  const [showButtonCopy, setShowButtonCopy] = useState(false);

  const [formData, setFormData] = useState({
    id: "",
    code: "",
    description: "",
    sapContractNumber: "",
    sapContractPosition: "",
    sapContractDispositionPosition: "",
    vehicle: 0,
    point: 0,
    addData: [],
    daysChange: DAYS_OF_WEEK.map(day => ({ ...day, checked: false })),
    error: {
      description: "",
      point: "",
      vehicle: "",
      days: "",
      addData: "",
      messageSave: ""
    }
  });

  // Initialize form data when selected route changes
  useEffect(() => {
    if (selected && selected.routes) {
      const routeData = selected.routes.map((res) => {
        const routeDays = DAYS_OF_WEEK.map((day) => {
          const isDaySelected = res.days.some(routeDay => routeDay.dayId === day.dayId);
          return { ...day, checked: isDaySelected };
        });

        return {
          collectPointId: res.collectPointId,
          collectPointName: res.collectPointName,
          pointNumber: res.pointNumber,
          days: routeDays,
        };
      });

      setFormData({
        id: selected.routeId || "",
        code: selected.code || "",
        description: selected.description || "",
        sapContractNumber: selected.sapContractNumber || "",
        sapContractPosition: selected.sapContractPosition || "",
        sapContractDispositionPosition: selected.sapContractDispositionPosition || "",
        vehicle: selected.vehicleId || 0,
        point: 0,
        addData: routeData,
        daysChange: DAYS_OF_WEEK.map(day => ({ ...day, checked: false })),
        error: {
          description: "",
          point: "",
          vehicle: "",
          days: "",
          addData: "",
          messageSave: ""
        }
      });
    } else {
      // Reset form for new route
      setFormData({
        id: "",
        code: "",
        description: "",
        sapContractNumber: "",
        sapContractPosition: "",
        sapContractDispositionPosition: "",
        vehicle: 0,
        point: 0,
        addData: [],
        daysChange: DAYS_OF_WEEK.map(day => ({ ...day, checked: false })),
        error: {
          description: "",
          point: "",
          vehicle: "",
          days: "",
          addData: "",
          messageSave: ""
        }
      });
    }
  }, [selected, selected.routes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleChangeBox = (dayIndex) => {
    setFormData(prev => ({
      ...prev,
      daysChange: prev.daysChange.map((day, index) => 
        index === dayIndex ? { ...day, checked: !day.checked } : day
      )
    }));
  };

  const handleChangeCheckNew = (dayIndex, routeIndex) => {
    setFormData(prev => ({
      ...prev,
      addData: prev.addData.map((route, rIndex) => 
        rIndex === routeIndex 
          ? {
              ...route,
              days: route.days.map((day, dIndex) => 
                dIndex === dayIndex ? { ...day, checked: !day.checked } : day
              )
            }
          : route
      )
    }));
  };

  const handleAdd = () => {
    const { point, daysChange } = formData;
    const selectedDays = daysChange.filter(day => day.checked);
    const selectedPoint = points.find(p => p.collectPointId === point);

    const newError = { ...formData.error };

    if (selectedDays.length === 0) {
      newError.days = "*Selecciona al menos un día";
    } else {
      newError.days = "";
    }

    if (point === 0) {
      newError.point = "*Selecciona un punto";
    } else {
      newError.point = "";
    }

    if (point !== 0 && selectedDays.length > 0) {
      const newRoute = {
        collectPointId: selectedPoint.collectPointId,
        collectPointName: selectedPoint.collectPointDescription,
        pointNumber: selectedPoint.pointNumber,
        days: daysChange.map(day => ({ ...day }))
      };

      setFormData(prev => ({
        ...prev,
        addData: [...prev.addData, newRoute],
        point: 0,
        daysChange: DAYS_OF_WEEK.map(day => ({ ...day, checked: false })),
        error: {
          ...prev.error,
          days: "",
          point: "",
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        error: newError
      }));
    }
  };

  const handleDelete = (index) => {
    setFormData(prev => ({
      ...prev,
      addData: prev.addData.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    const { addData, description } = formData;
    
    if (!description.trim()) {
      setFormData(prev => ({
        ...prev,
        error: { ...prev.error, description: "*Completar el campo" }
      }));
      return;
    }

    const routeArray = addData.map((route) => {
      const selectedDays = route.days.filter(day => day.checked).map(day => day.dayId);
      return {
        collectPointId: route.collectPointId,
        dayId: selectedDays,
      };
    });

    const routeData = {
      vehicleId: formData.vehicle === 0 ? null : formData.vehicle,
      description: formData.description,
      routeCode: formData.code,
      sapContractNumber: formData.sapContractNumber,
      sapContractPosition: Number(formData.sapContractPosition) || 0,
      sapContractDispositionPosition: Number(formData.sapContractDispositionPosition) || 0,
      routeArray: routeArray,
    };

    if (formData.id) {
      routeData.routeId = formData.id;
    }

    const result = await doSave(routeData);
    if (result && !result.success) {
      setFormData(prev => ({
        ...prev,
        error: { ...prev.error, messageSave: result.error }
      }));
    } else {
      setSelected(null);
    }
  };

  const handleCopy = () => {
    setFormData(prev => ({
      ...prev,
      id: "",
      code: "(COPIA)" + prev.code,
      description: "(COPIA)" + prev.description,
      vehicle: 0,
      error: { ...prev.error, messageSave: "" }
    }));
    setShowButtonCopy(false);
  };

  const onDragEnd = (result) => {
    const { destination, source } = result;
    
    if (!destination || (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )) {
      return;
    }

    setFormData(prev => {
      const newAddData = [...prev.addData];
      const [removed] = newAddData.splice(source.index, 1);
      newAddData.splice(destination.index, 0, removed);
      
      return {
        ...prev,
        addData: newAddData
      };
    });
  };

  return (
    <div className={classes.contentForm}>
      <Grid container>
        <Grid item xs={12}>
          <div className="inputs-form">
            <Grid container>
              <Grid item xs={2}>
                <label>ID </label>
              </Grid>
              <Grid item xs={10}>
                <TextField
                  disabled
                  name="id"
                  value={formData.id}
                  onChange={handleChange}
                  variant="outlined"
                  className={classes.textFielInput + " blocked-id"}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className="inputs-form">
            <Grid container>
              <Grid item xs={2}>
                <label>Código </label>
              </Grid>
              <Grid item xs={10}>
                <TextField
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  variant="outlined"
                  inputProps={{
                    maxLength: 250,
                  }}
                  className={classes.textFielInput}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className="inputs-form">
            <Grid container>
              <Grid item xs={2}>
                <label>Descripción </label>
              </Grid>
              <Grid item xs={10}>
                <TextField
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  variant="outlined"
                  inputProps={{
                    maxLength: 250,
                  }}
                  className={classes.textFielInput}
                />
                <p>
                  {formData.error.description || ""}
                </p>
              </Grid>
            </Grid>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className="inputs-form">
            <Grid container>
              <Grid item xs={2}>
                <label>N° Contrato</label>
              </Grid>
              <Grid item xs={10}>
                <TextField
                  name="sapContractNumber"
                  value={formData.sapContractNumber}
                  onChange={handleChange}
                  variant="outlined"
                  inputProps={{
                    maxLength: 10,
                  }}
                  className={classes.textFielInput}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className="inputs-form">
            <Grid container>
              <Grid item xs={2}>
                <label>Posición de recolección </label>
              </Grid>
              <Grid item xs={10}>
                <TextField
                  name="sapContractPosition"
                  value={formData.sapContractPosition}
                  onChange={handleChange}
                  variant="outlined"
                  inputProps={{
                    maxLength: 6,
                  }}
                  className={classes.textFielInput}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className="inputs-form">
            <Grid container>
              <Grid item xs={2}>
                <label>Posición de disposición</label>
              </Grid>
              <Grid item xs={10}>
                <TextField
                  name="sapContractDispositionPosition"
                  value={formData.sapContractDispositionPosition}
                  onChange={handleChange}
                  variant="outlined"
                  inputProps={{
                    maxLength: 6,
                  }}
                  className={classes.textFielInput}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className="inputs-form">
            <Grid container>
              <Grid item xs={2}>
                <label>Vehiculo </label>
              </Grid>
              <Grid item xs={10}>
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                >
                  <Select
                    name="vehicle"
                    value={formData.vehicle}
                    onChange={handleChange}
                  >
                    <MenuItem value={0}>Selecciona una opción</MenuItem>
                    {vehicles.map((item) => {
                      return (
                        <MenuItem key={item.vehicleId} value={item.vehicleId}>
                          {item.plateNumber}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <p>
                  {formData.error.messageSave || ""}
                </p>
              </Grid>
            </Grid>
          </div>
        </Grid>
      </Grid>
      <div>
        <h3 className={classes.title}>Puntos de Recolección</h3>
        <Grid container>
          <Grid item xs={12} className={classes.detailItem}>
            <Grid container>
              <Grid item xs={1}>
                <p>#</p>
              </Grid>
              <Grid
                item
                xs={5}
                style={{
                  paddingRight: "10px",
                  alignItems: "center",
                }}
              >
                <FormControl
                  variant="outlined"
                  className={classes.formControl}
                >
                  <Select
                    name="point"
                    value={formData.point}
                    onChange={handleChange}
                  >
                    <MenuItem value={0}>Selecciona una opción</MenuItem>
                    {points.map((item) => {
                      return (
                        <MenuItem key={item.collectPointId} value={item.collectPointId}>
                          {`${item.pointNumber}, ${item.collectPointDescription}`}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <p style={{ marginBottom: "0" }}>
                  {formData.error.point || ""}
                </p>
              </Grid>
              <Grid
                item
                xs={5}
                style={{
                  paddingLeft: "10px",
                }}
              >
                <Grid container style={{ justifyContent: "flex-end" }}>
                  {DAYS_OF_WEEK.map((day, index) => (
                    <Grid
                      key={day.dayId}
                      item
                      xs={1}
                      className={classes.contentCheckBox}
                    >
                      <Grid container>
                        <Grid item xs={12}>
                          {day.short}
                        </Grid>
                        <Grid item xs={12}>
                          <Checkbox
                            checked={formData.daysChange[index].checked || false}
                            className={classes.checkBox}
                            onChange={() => handleChangeBox(index)}
                            color="primary"
                            inputProps={{ "aria-label": `${day.dayName} checkbox` }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
                <p style={{ marginBottom: "0" }}>
                  {formData.error.days || ""}
                </p>
              </Grid>
              <Grid item xs={1}>
                <button
                  onClick={handleAdd}
                  className={classes.iconAddContent}
                >
                  <AddIcon />
                </button>
              </Grid>
              <p className="err">{formData.error.addData}</p>
            </Grid>
          </Grid>
          <Grid item xs={12} className={classes.listContent}>
            <List className={classes.listContainer}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Column
                  key={selected.routeId}
                  selected={selected}
                  addData={formData.addData}
                  handleChangeCheckNew={handleChangeCheckNew}
                  handleDelete={handleDelete}
                />
              </DragDropContext>
            </List>
          </Grid>
        </Grid>
      </div>
      <div className={classes.contentButton}>
        {formData.id && showButtonCopy && (
          <button onClick={handleCopy}>Copiar</button>
        )}
        <button onClick={() => setSelected(null)}>Cancelar</button>
        <button onClick={handleSave}>Guardar</button>
      </div>
    </div>
  );
}

export default FormRoutes;